import { ClientPrintJob, InstalledPrinter, JSPrintManager, WSStatus } from 'jsprintmanager';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';

export enum PrintManagerStatus {
  DISABLED,
  CONNECTING,
  CONNECTED,
  FAILED,
}

export type ConfiguredPrinter = {
  name: string;
  paper: string;
  paperSize: { width: number; height: number; dpi: number };
  rotation?: string;
  offset?: { x: number; y: number };
  deviceName: string;
  batchZPL?: string;
};

export type PrintAttemptResult =
  | { kind: 'jspm' }
  | { kind: 'browser-fallback'; reason: 'disabled' | 'failed' | 'timeout' | 'error' };

type PrintBrowserFallback = () => void;

type PrintViaJspmArgs = { printer: ConfiguredPrinter; zpl: string; timeoutMs?: number };

type TryPrintArgs = PrintViaJspmArgs & { fallback: PrintBrowserFallback };

type PrintManagerContext = {
  status: PrintManagerStatus;
  printers: ConfiguredPrinter[];
  /** Optimistic JSPM dispatch with browser-print fallback, called per user click. Races a JSPM ClientPrintJob against a short timeout + AbortController and falls back to the supplied browser-print function. Returns which path the print took. */
  tryPrint: (args: TryPrintArgs) => Promise<PrintAttemptResult>;
};

export const PrintManagerContext = createContext<PrintManagerContext>({
  status: PrintManagerStatus.DISABLED,
  printers: [],
  tryPrint: async ({ fallback }) => {
    fallback();
    return { kind: 'browser-fallback', reason: 'disabled' };
  },
});

const DEFAULT_PRINT_BUDGET_MS = 1500;
const STATUS_SAFETY_TIMEOUT_MS = 3000;

const handleSocketOpen = async (printConfig): Promise<ConfiguredPrinter[]> => {
  const printers = (await JSPrintManager.getPrinters()) as string[];
  const allowedPrinters = printers.filter((printer) => !printConfig.excludeList?.includes(printer));

  const withPages = await Promise.all(
    allowedPrinters.map((name) => JSPrintManager.getDefaultPaperName(name).then((paper) => ({ name, paper })))
  );

  const withPaperSizes = await Promise.all(
    withPages.map((printer) =>
      JSPrintManager.getPaperInfo(printer.name, printer.paper).then((paperSize) => ({ ...printer, paperSize }))
    )
  );

  const configuredPrinters = withPaperSizes.map((printer) => {
    if (printConfig.printers?.[printer.name]) {
      return { ...printer, ...printConfig.printers[printer.name], deviceName: printer.name };
    }

    return { ...printer, deviceName: printer.name };
  });

  return configuredPrinters;
};

const dispatchJspmJob = ({ printer, zpl }: PrintViaJspmArgs, signal: AbortSignal): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('aborted-before-dispatch'));
      return;
    }

    const cpj = new ClientPrintJob();
    cpj.clientPrinter = new InstalledPrinter(printer.deviceName);
    const commands = Uint8Array.from(Array.from(zpl).map((letter) => letter.charCodeAt(0)));
    cpj.binaryPrinterCommands = commands;

    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    cpj.onFinished = () => settle(resolve);
    cpj.onError = (err) => settle(() => reject(err ?? new Error('jspm-error')));

    signal.addEventListener('abort', () => settle(() => reject(new Error('aborted'))), { once: true });

    cpj.sendToClient();
  });
};

export default function PrintManagerProvider({ children }: PropsWithChildren) {
  const [printers, setPrinters] = useState<ConfiguredPrinter[]>([]);
  const [status, setStatus] = useState(PrintManagerStatus.DISABLED);

  const flags = useFlags();
  const printConfig = flags['printManagerConfiguration'];

  const enabled = printConfig?.enabled;
  // Status snapshot so tryPrint reads fresh values without re-creating
  // every render.
  const statusRef = useRef(status);
  statusRef.current = status;

  const tryPrint = useCallback(async (args: TryPrintArgs): Promise<PrintAttemptResult> => {
    const current = statusRef.current;

    // Fast paths — known no-JSPM states bypass the dispatch entirely.
    if (current === PrintManagerStatus.DISABLED) {
      args.fallback();
      return { kind: 'browser-fallback', reason: 'disabled' };
    }
    if (current === PrintManagerStatus.FAILED) {
      args.fallback();
      return { kind: 'browser-fallback', reason: 'failed' };
    }

    // CONNECTED or CONNECTING — dispatch optimistically, race against budget.
    const controller = new AbortController();
    const budget = args.timeoutMs ?? DEFAULT_PRINT_BUDGET_MS;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('timeout')), budget);
    });

    try {
      await Promise.race([dispatchJspmJob(args, controller.signal), timeoutPromise]);
      if (timeoutId) clearTimeout(timeoutId);
      return { kind: 'jspm' };
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);
      controller.abort();
      args.fallback();
      const isTimeout = err instanceof Error && err.message === 'timeout';
      return { kind: 'browser-fallback', reason: isTimeout ? 'timeout' : 'error' };
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (JSPrintManager.websocket_status == WSStatus.Open) {
      return;
    }

    JSPrintManager.license_url = window.location.origin + '/.redwood/functions/jspm';

    JSPrintManager.auto_reconnect = true;

    setStatus(PrintManagerStatus.CONNECTING);

    // Safety net for half-handshake scenarios (v9 web library against a v7 desktop app) where the WSS connects but never reaches a terminal state. Keeps `status` accurate so later clicks hit the fast `FAILED` path. Distinct from tryPrint's per-click timeout.
    const failTimer = setTimeout(() => {
      setStatus((s) => (s === PrintManagerStatus.CONNECTING ? PrintManagerStatus.FAILED : s));
    }, STATUS_SAFETY_TIMEOUT_MS);

    JSPrintManager.start(true, printConfig.host, printConfig.port).catch((e) => {
      console.error('JS Print Manager:', e);
      setStatus(PrintManagerStatus.FAILED);
    });

    const ws = JSPrintManager.WS;
    if (!ws) {
      console.error('JS Print Manager: WS not initialized after start(); skipping handler wiring');
      setStatus(PrintManagerStatus.FAILED);
      clearTimeout(failTimer);
      return;
    }

    ws.onConnectionFailed = (wsStatus, wasConnected) => {
      console.error('JS Print Manager: connection failed', { wsStatus, wasConnected });
      setStatus(PrintManagerStatus.FAILED);
    };

    ws.onStatusChanged = function () {
      console.debug(`JS Print Manager: Status updated to ${JSPrintManager.websocket_status}`);

      switch (JSPrintManager.websocket_status) {
        case WSStatus.Open: {
          handleSocketOpen(printConfig).then((printers) => {
            console.debug('JS Print Manager: Found Printers', printers);

            setPrinters(printers);
          });

          setStatus(PrintManagerStatus.CONNECTED);
          break;
        }
        case WSStatus.Closed:
        case WSStatus.Blocked:
          setStatus(PrintManagerStatus.FAILED);
          break;
        case WSStatus.NotInstalled:
          console.error('JS Print Manager: client app is not installed on this device');
          JSPrintManager.auto_reconnect = false;
          setStatus(PrintManagerStatus.FAILED);
          break;
        case WSStatus.CertificateError:
          console.error('JS Print Manager: CA/server certificate error — reissue or reinstall the CA cert');
          JSPrintManager.auto_reconnect = false;
          setStatus(PrintManagerStatus.FAILED);
          break;
        case WSStatus.ConnectionError:
          console.error('JS Print Manager: websocket connection error');
          setStatus(PrintManagerStatus.FAILED);
          break;
      }
    };

    return () => clearTimeout(failTimer);
  }, [enabled, printConfig]);

  return <PrintManagerContext.Provider value={{ printers, status, tryPrint }}>{children}</PrintManagerContext.Provider>;
}

export const usePrintManager = () => useContext(PrintManagerContext);
